import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import type { noop } from "lodash";
import { APPLICATIONS_URL } from "constants/routes";
import {
  hasDeleteWorkflowPermission,
  hasExportWorkflowPermission,
} from "ee/utils/permissionHelpers";
import { Colors } from "constants/Colors";
import { toast } from "@appsmith/ads";
import type { ThemeProp } from "WidgetProvider/types";
import { DISCORD_URL, DOCS_BASE_URL } from "constants/ThirdPartyConstants";
import type { MenuItemData } from "pages/Editor/EditorName/NavigationMenuItem";
import { MenuTypes } from "pages/Editor/EditorName/types";
import { getCurrentWorkflow } from "ee/selectors/workflowSelectors";
import { deleteWorkflow } from "ee/actions/workflowActions";
import { exportWorkflowAsJSONFile } from "ee/utils/workflowHelpers";

export interface NavigationMenuDataProps extends ThemeProp {
  editMode: typeof noop;
}

export const useNavigationMenuData = ({
  editMode,
}: NavigationMenuDataProps): MenuItemData[] => {
  const dispatch = useDispatch();
  const history = useHistory();

  const currentWorkflow = useSelector(getCurrentWorkflow);
  const workflowId = currentWorkflow?.id;
  const isWorkflowIdPresent = !!(workflowId && workflowId.length > 0);
  const hasExportPermission = hasExportWorkflowPermission(
    currentWorkflow?.userPermissions || [],
  );

  const openExternalLink = (link: string) => {
    if (link) {
      window.open(link, "_blank");
    }
  };

  const deleteWorkflowHandler = () => {
    if (workflowId && workflowId.length > 0) {
      dispatch(deleteWorkflow({ id: workflowId }));
      history.push(APPLICATIONS_URL);
    } else {
      toast.show("Error while deleting Workflow", {
        kind: "error",
      });
    }
  };

  const exportWorkflow = () => {
    if (!currentWorkflow) return;

    exportWorkflowAsJSONFile({
      workflowId: currentWorkflow.id,
      onSuccessCb: () => {
        toast.show(`Successfully exported ${currentWorkflow.name}`, {
          kind: "success",
        });
      },
    });
  };

  return [
    {
      text: "Home",
      onClick: () => history.replace(APPLICATIONS_URL),
      type: MenuTypes.MENU,
      isVisible: true,
    },
    {
      text: "divider_1",
      type: MenuTypes.MENU_DIVIDER,
      isVisible: true,
    },
    {
      text: "Rename",
      onClick: editMode,
      type: MenuTypes.MENU,
      isVisible: true,
    },
    hasExportPermission && {
      text: "Export",
      onClick: exportWorkflow,
      type: MenuTypes.MENU,
      isVisible: isWorkflowIdPresent,
    },
    {
      text: "Help",
      type: MenuTypes.PARENT,
      isVisible: true,
      children: [
        {
          text: "Community forum",
          onClick: () => openExternalLink("https://community.appsmith.com/"),
          type: MenuTypes.MENU,
          isVisible: true,
          isOpensNewWindow: true,
        },
        {
          text: "Discord channel",
          onClick: () => openExternalLink(DISCORD_URL),
          type: MenuTypes.MENU,
          isVisible: true,
          isOpensNewWindow: true,
        },
        {
          text: "Github",
          onClick: () =>
            openExternalLink("https://github.com/appsmithorg/appsmith/"),
          type: MenuTypes.MENU,
          isVisible: true,
          isOpensNewWindow: true,
        },
        {
          text: "Documentation",
          onClick: () => openExternalLink(DOCS_BASE_URL),
          type: MenuTypes.MENU,
          isVisible: true,
          isOpensNewWindow: true,
        },
      ],
    },
    hasDeleteWorkflowPermission(currentWorkflow?.userPermissions) && {
      text: "Delete workflow",
      confirmText: "Are you sure?",
      onClick: deleteWorkflowHandler,
      type: MenuTypes.RECONFIRM,
      isVisible: isWorkflowIdPresent,
      style: { color: Colors.ERROR_RED },
    },
  ].filter(Boolean) as MenuItemData[];
};
